import {Box, Grid, Typography, TypographyProps} from '@mui/material';
import {Page, PageSeo, PageType} from '../../components/common/container';
import {ProseSmall, Title} from '../../components/common/text';
import {Trans, useTranslation} from 'next-i18next';
import {
    CONTACT,
    KUNEKUNE_INFO,
    KUNEKUNE_PUBLISHER,
    OVH_INFO,
    VERCEL_INFO,
} from '../../constants';
import {GetStaticProps} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';

export const getStaticProps: GetStaticProps<{}> = async (context) => {
    const translation = await serverSideTranslations(context.locale, [
        'common',
        'legal-notice',
    ]);
    return {
        props: translation,
        revalidate: 86400, // 1 jour
    };
};

export default function Mention() {
    return (
        <Page>
            <PageSeo page={PageType.MENTION} />
            <LegalNotice />
        </Page>
    );
}

export function LegalNotice() {
    const {t} = useTranslation('legal-notice');

    return (
        <>
            <Title>{t('title')}</Title>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 900,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginTop: 2,
                    marginBottom: 8,
                    gap: 2,
                    paddingRight: 4,
                    paddingLeft: 4,
                }}
            >
                <Updated />
                <Grid
                    container
                    spacing={2}
                    sx={{
                        borderTopStyle: 'solid',
                        borderBottomStyle: 'solid',
                        borderTopWidth: 1,
                        borderBottomWidth: 1,
                    }}
                >
                    <Grid item xs={4}>
                        <LegalNoticeRoleTitle>{t('editor.title')}</LegalNoticeRoleTitle>
                    </Grid>
                    <Grid item xs={8}>
                        <LegalNoticeEditor />
                    </Grid>
                    <Grid item xs={4}>
                        <LegalNoticeRoleTitle>{t('publisher.title')}</LegalNoticeRoleTitle>
                    </Grid>
                    <Grid item xs={8}>
                        <LegalNoticePublisher />
                    </Grid>
                    <Grid item xs={4}>
                        <LegalNoticeRoleTitle>{t('host.title')}</LegalNoticeRoleTitle>
                    </Grid>
                    <Grid item xs={8}>
                        <LegalHost />
                    </Grid>
                    <Grid item xs={4}>
                        <LegalNoticeRoleTitle>{t('host-front.title')}</LegalNoticeRoleTitle>
                    </Grid>
                    <Grid item xs={8}>
                        <LegalHostFront />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

function Updated() {
    const {t} = useTranslation('legal-notice');

    return (
        <Typography variant="caption">
            {t('updated', {date: new Date(Date.UTC(2023, 6, 30, 0, 0, 0))})}
        </Typography>
    );
}

function LegalNoticeEditor() {
    const {t} = useTranslation('legal-notice');

    return (
        <Box>
            <ProseSmall>
                <Trans
                    t={t}
                    i18nKey="editor.details.name"
                    values={{name: KUNEKUNE_INFO.NAME}}
                />
            </ProseSmall>
            <ProseSmall>
                {t('editor.details.sas', {companyCapital: KUNEKUNE_INFO.CAPITAL})}
            </ProseSmall>
            <ProseSmall>
                {t('editor.details.address', {address: KUNEKUNE_INFO.ADDRESS})}
            </ProseSmall>
            <ProseSmall>
                {t('editor.details.registrar', {
                    registrarShort: KUNEKUNE_INFO.REGISTRAR_SHORT,
                    id: KUNEKUNE_INFO.ID,
                })}
            </ProseSmall>
            <ProseSmall>{t('editor.details.vat', {vat: KUNEKUNE_INFO.VAT})}</ProseSmall>
            <ProseSmall>{t('editor.details.email', {email: CONTACT.EMAIL})}</ProseSmall>
            <ProseSmall>{t('editor.details.phone', {phone: CONTACT.PHONE})}</ProseSmall>
        </Box>
    );
}

function LegalNoticePublisher() {
    const {t} = useTranslation('legal-notice');

    return (
        <Box>
            <ProseSmall>
                {t('publisher.details.name', {name: KUNEKUNE_PUBLISHER.NAME})}
            </ProseSmall>
            <ProseSmall>
                {t('publisher.details.email', {email: KUNEKUNE_PUBLISHER.EMAIL})}
            </ProseSmall>
            <ProseSmall>
                {t('publisher.details.phone', {phone: KUNEKUNE_PUBLISHER.PHONE})}
            </ProseSmall>
        </Box>
    );
}

function LegalHost() {
    const {t} = useTranslation('legal-notice');

    return (
        <Box>
            <ProseSmall>
                <Trans t={t} i18nKey="host.details.name" values={{name: OVH_INFO.NAME}} />
            </ProseSmall>
            <ProseSmall>
                {t('host.details.sas', {companyCapital: OVH_INFO.CAPITAL})}
            </ProseSmall>
            <ProseSmall>{t('host.details.address', {address: OVH_INFO.ADDRESS})}</ProseSmall>
            <ProseSmall>
                {t('host.details.registrar', {
                    registrarShort: OVH_INFO.REGISTRAR_SHORT,
                    id: OVH_INFO.ID,
                })}
            </ProseSmall>
            <ProseSmall>{t('host.details.vat', {vat: OVH_INFO.VAT})}</ProseSmall>
        </Box>
    );
}

function LegalHostFront() {
    const {t} = useTranslation('legal-notice');

    return (
        <Box>
            <ProseSmall>
                <Trans
                    t={t}
                    i18nKey="host-front.details.name"
                    values={{name: VERCEL_INFO.NAME}}
                />
            </ProseSmall>
            <ProseSmall>{t('host-front.details.status')}</ProseSmall>
            <ProseSmall>
                {t('host-front.details.address', {address: VERCEL_INFO.ADDRESS})}
            </ProseSmall>
            <ProseSmall>
                {t('host-front.details.email', {email: VERCEL_INFO.EMAIL})}
            </ProseSmall>
        </Box>
    );
}

function LegalNoticeRoleTitle(props: Omit<TypographyProps, 'sx'>) {
    return <Typography sx={{fontSize: 18, fontWeight: 'bold'}} {...props} />;
}
